import React, { useEffect, useState } from "react";
import { FileText, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Badge, statusTone, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const run = async (fn, msg, reload) => {
  try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
};

async function openDoc(aid, i) {
  try {
    const { data } = await api.get(`/franchise/applications/${aid}/documents/${i}`, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  } catch { toast.error("Could not open document"); }
}

function AppDetail({ a, onClose, reload }) {
  const [remark, setRemark] = useState("");
  if (!a) return null;
  const act = (status) => run(() => api.post(`/franchise/admin/applications/${a.id}/status`, { status, remark }), `Application ${status.replace("_", " ")}`, () => { onClose(); reload(); });
  const rows = [["Institute", `${a.institute_name} (${a.institute_type})`], ["Address", `${a.address}, ${a.district}, ${a.state} – ${a.pincode}`], ["Representative", `${a.rep_name}, ${a.rep_designation}`], ["Contact", `${a.mobile} • ${a.email}`], ["Registration", [a.registration_type, a.registration_number, a.gstin].filter(Boolean).join(" • ") || "—"], ["Facilities", a.facilities], ["Capacity", a.student_capacity], ["Payment", a.payment_status === "paid" ? `Paid ₹${a.payment?.amount} • ${a.payment?.razorpay_payment_id}` : "Not paid"]];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="admin-app-detail">
        <DialogHeader><DialogTitle>{a.ref_no} <Badge tone={statusTone(a.status)}>{a.status.replace(/_/g, " ")}</Badge></DialogTitle></DialogHeader>
        <dl className="grid grid-cols-[130px_1fr] gap-y-2 text-sm">{rows.map(([k, v]) => <React.Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
        <div><div className="text-sm font-semibold mb-2">Documents</div>
          <div className="flex flex-wrap gap-2">{a.documents.map((d, i) => <Button key={i} size="sm" variant="outline" onClick={() => openDoc(a.id, i)} data-testid={`admin-doc-${i}`}><FileText size={14} className="mr-1" /> {d.name}</Button>)}</div>
        </div>
        {!["approved", "rejected"].includes(a.status) && (
          <div className="space-y-3 border-t border-border pt-4">
            <Textarea placeholder="Remark for the applicant (required to reject or request documents)" value={remark} onChange={(e) => setRemark(e.target.value)} data-testid="admin-app-remark" />
            <div className="flex flex-wrap gap-2">
              <Button style={{ backgroundColor: "#059669", color: "#fff" }} disabled={a.payment_status !== "paid"} onClick={() => act("approved")} data-testid="admin-app-approve">Approve & activate</Button>
              <Button variant="outline" onClick={() => act("documents_required")} data-testid="admin-app-docs">Request documents</Button>
              <Button variant="outline" className="text-crimson" onClick={() => act("rejected")} data-testid="admin-app-reject">Reject</Button>
            </div>
            {a.payment_status !== "paid" && <p className="text-xs text-muted-foreground">Approval unlocks after the franchise fee payment is confirmed.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Applications() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/franchise/admin/applications").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr>{["Reference", "Institute", "Location", "Submitted", "Payment", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No applications yet.</td></tr>}
          {list.map((a) => (
            <tr key={a.id} className="border-t border-border" data-testid={`admin-app-${a.id}`}>
              <td className="px-4 py-3 font-mono">{a.ref_no}</td><td className="px-4 py-3 font-semibold">{a.institute_name}</td>
              <td className="px-4 py-3">{a.district}, {a.state}</td><td className="px-4 py-3">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
              <td className="px-4 py-3"><Badge tone={a.payment_status === "paid" ? "green" : "amber"}>{a.payment_status}</Badge></td>
              <td className="px-4 py-3"><Badge tone={statusTone(a.status)}>{a.status.replace(/_/g, " ")}</Badge></td>
              <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => setOpen(a)} data-testid={`admin-app-open-${a.id}`}>Review</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <AppDetail a={open} onClose={() => setOpen(null)} reload={load} />
    </div>
  );
}

function Banks() {
  const [list, setList] = useState([]);
  const load = () => api.get("/franchise/admin/bank-accounts").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const act = (b, status) => {
    const remark = status === "rejected" ? window.prompt("Reason for rejection") : "";
    if (remark === null) return;
    if (status === "rejected" && !remark.trim()) { toast.error("Enter a reason to reject"); return; }
    run(() => api.post(`/franchise/admin/bank-accounts/${b.id}/status`, { status, remark }), `Bank account ${status}`, load);
  };
  return (
    <div className="bg-white border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr>{["Institute", "Holder", "Account / IFSC", "Fee payment", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No bank accounts submitted.</td></tr>}
          {list.map((b) => (
            <tr key={b.id} className="border-t border-border" data-testid={`admin-bank-${b.id}`}>
              <td className="px-4 py-3">{b.institute_name}</td><td className="px-4 py-3">{b.holder_name}</td>
              <td className="px-4 py-3 font-mono text-xs">{b.account_number_full}<br />{b.ifsc} {b.bank_name}</td>
              <td className="px-4 py-3 font-mono text-xs">{b.fee_payment_id || "—"}</td>
              <td className="px-4 py-3"><Badge tone={statusTone(b.status === "verified" ? "approved" : b.status)}>{b.status.replace("_", " ")}</Badge></td>
              <td className="px-4 py-3">{b.status === "pending_review" && <div className="flex gap-2"><Button size="sm" style={{ backgroundColor: "#059669", color: "#fff" }} onClick={() => act(b, "verified")} data-testid={`admin-bank-verify-${b.id}`}>Verify</Button><Button size="sm" variant="outline" onClick={() => act(b, "rejected")} data-testid={`admin-bank-reject-${b.id}`}>Reject</Button></div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Payouts() {
  const [list, setList] = useState([]);
  const load = () => api.get("/franchise/admin/withdrawals").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr>{["Requested", "Institute", "Amount", "To", "Status", "UTR / Error", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No withdrawal requests.</td></tr>}
          {list.map((w) => (
            <tr key={w.id} className="border-t border-border" data-testid={`admin-wd-${w.id}`}>
              <td className="px-4 py-3">{new Date(w.created_at).toLocaleDateString("en-IN")}</td><td className="px-4 py-3">{w.institute_name}</td>
              <td className="px-4 py-3 font-semibold"><Money amount={w.amount} /></td><td className="px-4 py-3 font-mono">{w.bank_masked}</td>
              <td className="px-4 py-3"><Badge tone={statusTone(w.status)}>{w.status}</Badge></td>
              <td className="px-4 py-3 text-xs">{w.utr || w.failure_reason || w.last_error || "—"}</td>
              <td className="px-4 py-3">
                {w.status === "requested" && <div className="flex gap-2">
                  <Button size="sm" style={{ backgroundColor: "#059669", color: "#fff" }} onClick={() => window.confirm(`Send ₹${w.amount} to ${w.bank_masked} via RazorpayX?`) && run(() => api.post(`/franchise/admin/withdrawals/${w.id}/approve`), "Payout initiated", load)} data-testid={`admin-wd-approve-${w.id}`}>Approve & pay</Button>
                  <Button size="sm" variant="outline" onClick={() => { const r = window.prompt("Reason"); if (r === null) return; if (!r.trim()) { toast.error("Enter a reason to reject"); return; } run(() => api.post(`/franchise/admin/withdrawals/${w.id}/reject`, { remark: r }), "Rejected", load); }} data-testid={`admin-wd-reject-${w.id}`}>Reject</Button>
                </div>}
                {w.status === "processing" && <Button size="sm" variant="ghost" onClick={() => run(() => api.post(`/franchise/admin/withdrawals/${w.id}/sync`), "Status refreshed", load)} data-testid={`admin-wd-sync-${w.id}`}><RefreshCw size={14} /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminFranchise() {
  return (
    <div data-testid="admin-franchise">
      <PageHeader title="Institute Franchise" subtitle="Review applications, verify bank accounts and process partner payouts. Institute commissions are confirmed in Commissions." />
      <Tabs defaultValue="apps">
        <TabsList className="mb-4"><TabsTrigger value="apps" data-testid="tab-apps">Applications</TabsTrigger><TabsTrigger value="banks" data-testid="tab-banks">Bank Verification</TabsTrigger><TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger></TabsList>
        <TabsContent value="apps"><Applications /></TabsContent>
        <TabsContent value="banks"><Banks /></TabsContent>
        <TabsContent value="payouts"><Payouts /></TabsContent>
      </Tabs>
    </div>
  );
}
